import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppSettings } from '../../config/app-settings.js';
import { setError, setLoading, setRememberMe } from '../../reducers/userReducers.jsx';
import API from '../../api/api.jsx';

const Login = () => {
	const context    = React.useContext(AppSettings);
	const { t }      = useTranslation();
	const navigate   = useNavigate();
	const dispatch   = useDispatch();
	const rememberMe = useSelector(state => state.login.rememberMe);

	// 로그인 정보
	const [userId, setUserId]       = useState('');
	const [password, setPassword]   = useState('');

	// 알림창
	const [showAlert, setShowAlert]       = useState(false);
	const [alertMessage, setAlertMessage] = useState('');

	useEffect(() => {
		context.handleSetAppSidebarNone(true);
		context.handleSetAppHeaderNone(true);
		context.handleSetAppFooterNone(true);
		context.handleSetAppThemeNone(true);
		context.handleSetAppContentClass('p-0');

		// 세션 스토리지에서 아이디 불러오기
        const storedUserId = sessionStorage.getItem('userId');
        if (storedUserId) {
            setUserId(storedUserId);
        }
	
		return () => {
			context.handleSetAppSidebarNone(false);
			context.handleSetAppHeaderNone(false);
			context.handleSetAppFooterNone(false);
			context.handleSetAppThemeNone(false);
			context.handleSetAppContentClass('');
		};
	}, []);

	// 아이디 저장 체크박스
	const handleRememberMeChange = () => {
		const newRememberMe = !rememberMe;

		// 세션 스토리지에 아이디 저장 또는 삭제
		if (newRememberMe) {
            sessionStorage.setItem('userId', userId);
        } else {
            sessionStorage.removeItem('userId');
        }

        dispatch(setRememberMe(newRememberMe));
	};

	const handleChangeUserId = (e) => {
		// 아이디가 변경될 때 세션 스토리지에 저장
        const newUserId = e.target.value;
        setUserId(newUserId);

        // 체크박스가 활성화된 경우에만 저장
        if (rememberMe) {
            sessionStorage.setItem('userId', newUserId);
        }
	};

	const handleChangePassword = (e) => {
		setPassword(e.target.value);
	};

	const generateRandomOtp = () => {
        return Math.floor(100000 + Math.random() * 900000);
    };
	

	let isHandlingClick = false; // 클릭 처리 여부를 나타내는 변수

	const handleSubmit = async (event) => {
		event.preventDefault();
	
		dispatch(setLoading(true));

		if (!userId) {
			const message = t('COMMON.아이디를 입력해주세요', { defaultValue: '아이디를 입력해주세요' });
			setAlertMessage(message);
			setShowAlert(true);

			// 일정 시간 후에 경고를 숨기도록 설정 (예: 3초 후)
			setTimeout(() => {
				setShowAlert(false);
				setAlertMessage('');
			}, 3000);

			return;
		};

		if (!password) {
			const message = t('COMMON.비밀번호를 입력해주세요', { defaultValue: '비밀번호를 입력해주세요' });
			setAlertMessage(message);
			setShowAlert(true);

			// 일정 시간 후에 경고를 숨기도록 설정 (예: 3초 후)
			setTimeout(() => {
				setShowAlert(false);
				setAlertMessage('');
			}, 3000);

			return;
		}

		if (isHandlingClick) return; // 클릭 처리 중이면 중복 클릭 방지

		isHandlingClick = true; // 클릭 처리 시작

		// API 호출 부분
		try {
			const response = await API.fetchLogin({
				userId,
				password
			});

			// 개발단계에서는 alert 형태로 확인
			console.log('userId : ' + userId + '  process.env.REACT_APP_ENV : ' + process.env.REACT_APP_ENV);
			if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'stage' || userId == 'admin@naver.com') {
				const generatedOtp = generateRandomOtp();
				alert(`Your OTP: ${generatedOtp}`);
				const randomOtp = String(generatedOtp);
				navigate('/login/otp', { state :  { response, randomOtp } });
			} else {
				// OTP ZALO로 발급 받는 부분
				const generate_OTP = async () => {
					try {
						await API.generateOTP(response)
						navigate('/login/otp', { state : response });
					} catch (error) {
						console.error('Error Generate OTP : ', error);
						const message = error.response.data.result.content.message;
						alert(message);
						return;
					};
				};

				// OTP 호출 함수 (해당 부분 살리면 ZALO OTP 성공)
				generate_OTP();
			}
		} catch (error) {
			// 인증 관련 에러 발생
			if (error.response.status === 401) {
				const error_msg = error.response.data.result.content.message;
				const message   = t(`COMMON.${error_msg}`, { defaultValue: error_msg });

				setAlertMessage(message);
				setShowAlert(true);
			
				// 일정 시간 후에 경고를 숨기도록 설정 (예: 3초 후)
				setTimeout(() => {
					setShowAlert(false);
					setAlertMessage('');
				}, 3000);
				return;
			}

			dispatch(setError(error));
		} finally {
			isHandlingClick = false; // 클릭 처리 종료
		}
	};

	return (
		<React.Fragment>
			<div className="login login-v2 fw-bold">
				<div className="login-cover">
					<div className="login-cover-img"></div>
					<div className="login-cover-bg"></div>
				</div>
		
				<div className="login-container">
					<div className="login-header">
						<div className="brand">
							<div className="d-flex align-items-center">
								<span className="logo"></span> <b>DIDO</b>
							</div>
							<small>DIDO Login Page</small>
						</div>
						<div className="icon">
							<i className="fa fa-lock"></i>
						</div>
					</div>

					{/* 커스텀 alert */}
					{showAlert && (
						<div className="alert alert-warning" role="alert" style={{ position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)', zIndex: '9999' }}>
							{alertMessage}
						</div>
					)}
					
					<div className="login-content">
						<form onSubmit={handleSubmit}>
							<div className="form-floating mb-20px">
								<input type="Email" className="form-control fs-13px h-45px border-0" placeholder="Email Address" id="email" value={userId} onChange={handleChangeUserId}/>
								<label htmlFor="emailAddress" className="d-flex align-items-center text-gray-600 fs-13px">Email Address</label>
							</div>
							<div className="form-floating mb-20px">
								<input type="password" className="form-control fs-13px h-45px border-0" placeholder="Password" id="password" value={password} onChange={handleChangePassword}/>
								<label htmlFor="emailAddress" className="d-flex align-items-center text-gray-600 fs-13px">Password</label>
							</div>

							<div className="form-check mb-20px">
								<input 
									type="checkbox" 
									id="rememberMe" 
									className="form-check-input border-0 checkbox-input"
									checked={rememberMe}
									onChange={handleRememberMeChange}
								/>
								<label className="form-check-label fs-13px text-gray-500" htmlFor="rememberMe">
									Remember Me
								</label>
							</div>
							<div className="mb-20px">
								<button type="submit" className="btn btn-success d-block w-100 h-45px btn-lg">Sign me in</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</React.Fragment>
	)
};

export default Login;
